/**
 * Silent Key — super admin only. List, add, remove silent key flag on UserV2.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Search, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { userService, type Member } from "@/services/userService";
import type { User } from "@/services/authService";

export function SilentKeyPage() {
  React.useEffect(() => {
    document.title = "Silent Key - ORARI";
  }, []);

  const [rows, setRows] = React.useState<Member[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchList, setSearchList] = React.useState("");
  const [hasNext, setHasNext] = React.useState(false);
  const [hasPrevious, setHasPrevious] = React.useState(false);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [previousCursor, setPreviousCursor] = React.useState<string | null>(null);

  const loadList = React.useCallback(
    async (opts: { cursor?: string; direction: "next" | "previous"; search?: string }) => {
      setLoading(true);
      try {
        const res = await userService.fetchSilentKeyUsers({
          cursor: opts.cursor,
          direction: opts.direction,
          search: opts.search?.trim() || undefined,
        });
        setRows(res.data);
        setHasNext(res.hasNext);
        setHasPrevious(res.hasPrevious);
        setNextCursor(res.nextCursor);
        setPreviousCursor(res.previousCursor);
      } catch (e) {
        console.error(e);
        toast.error(e instanceof Error ? e.message : "Gagal memuat daftar Silent Key");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  React.useEffect(() => {
    loadList({ direction: "next", search: searchList || undefined });
  }, [searchList, loadList]);

  const goNext = () => {
    if (!nextCursor) return;
    loadList({ cursor: nextCursor, direction: "next", search: searchList || undefined });
  };

  const goPrev = () => {
    if (!previousCursor) return;
    loadList({ cursor: previousCursor, direction: "previous", search: searchList || undefined });
  };

  const [removeTarget, setRemoveTarget] = React.useState<Member | null>(null);
  const [removing, setRemoving] = React.useState(false);

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await userService.updateUserSilentKey(removeTarget._id, false);
      toast.success("Silent Key dinonaktifkan", { description: removeTarget.callsign });
      setRemoveTarget(null);
      await loadList({ direction: "next", search: searchList || undefined });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus Silent Key");
    } finally {
      setRemoving(false);
    }
  };

  const [addOpen, setAddOpen] = React.useState(false);
  const [pickSearch, setPickSearch] = React.useState("");
  const [pickLoading, setPickLoading] = React.useState(false);
  const [pickResults, setPickResults] = React.useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = React.useState<Member | null>(null);
  const [detailUser, setDetailUser] = React.useState<User | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [reportBy, setReportBy] = React.useState("");
  const [reportDate, setReportDate] = React.useState("");
  const [reportNotes, setReportNotes] = React.useState("");

  React.useEffect(() => {
    if (!addOpen) {
      setPickSearch("");
      setPickResults([]);
      setSelectedMember(null);
      setDetailUser(null);
      setReportBy("");
      setReportDate("");
      setReportNotes("");
      return;
    }
  }, [addOpen]);

  const runPickSearch = React.useCallback(async () => {
    const q = pickSearch.trim().toUpperCase();
    if (!q) {
      setPickResults([]);
      return;
    }
    setPickLoading(true);
    try {
      const res = await userService.fetchMembers({
        search: q,
        isSilentKey: false,
        direction: "next",
      });
      setPickResults(res.data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal mencari anggota");
      setPickResults([]);
    } finally {
      setPickLoading(false);
    }
  }, [pickSearch]);

  const openDetailForMember = async (m: Member) => {
    setSelectedMember(m);
    setDetailLoading(true);
    setDetailUser(null);
    try {
      const u = await userService.fetchUserDetailById(m._id);
      setDetailUser(u);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal memuat detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const confirmAdd = async () => {
    if (!selectedMember) return;
    const by = reportBy.trim();
    const dt = reportDate.trim();
    if (!by) {
      toast.error("Isi Source trusted information.");
      return;
    }
    if (!dt) {
      toast.error("Isi tanggal report.");
      return;
    }
    setAdding(true);
    try {
      await userService.updateUserSilentKey(selectedMember._id, {
        isSilentKey: true,
        silentKeyReportedBy: by,
        silentKeyReportedAt: dt,
        silentKeyNotes: reportNotes.trim() || undefined,
      });
      toast.success("Silent Key diaktifkan", { description: selectedMember.callsign });
      setAddOpen(false);
      await loadList({
        cursor: undefined,
        direction: "next",
        search: searchList || undefined,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menambah Silent Key");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex-1 space-y-4 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <KeyRound className="h-8 w-8" />
            Silent Key
          </h1>
          <p className="text-muted-foreground mt-1">
            Kelola anggota yang ditandai sebagai Silent Key (hanya super admin).
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Tambah Silent Key
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daftar Silent Key</CardTitle>
          <CardDescription>Anggota dengan flag isSilentKey aktif.</CardDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Input
              placeholder="Cari callsign (exact)..."
              value={searchList}
              onChange={(e) => setSearchList(e.target.value.toUpperCase())}
              className="max-w-xs font-mono"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              title="Muat ulang"
              onClick={() => loadList({ direction: "next", search: searchList || undefined })}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Tidak ada data Silent Key.</p>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Callsign</TableHead>
                      <TableHead>Nama</TableHead>
                      <TableHead>Source trusted information</TableHead>
                      <TableHead>Tanggal report</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Provinsi</TableHead>
                      <TableHead>Kabupaten</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right w-[100px]">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r._id}>
                        <TableCell className="font-mono text-sm">{r.callsign}</TableCell>
                        <TableCell>{r.nama_lengkap}</TableCell>
                        <TableCell className="text-sm">{r.silentKeyReportedBy ?? "-"}</TableCell>
                        <TableCell className="text-sm">
                          {r.silentKeyReportedAt ? new Date(r.silentKeyReportedAt).toLocaleDateString("id-ID") : "-"}
                        </TableCell>
                        <TableCell className="text-sm max-w-[260px] truncate" title={r.silentKeyNotes ?? undefined}>
                          {r.silentKeyNotes ?? "-"}
                        </TableCell>
                        <TableCell>{r.provinsi}</TableCell>
                        <TableCell>{r.kabupaten}</TableCell>
                        <TableCell className="text-sm">{r.status_callsign}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            title="Hapus Silent Key"
                            onClick={() => setRemoveTarget(r)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between pt-4">
                <Button variant="outline" size="sm" disabled={!hasPrevious} onClick={goPrev}>
                  Sebelumnya
                </Button>
                <Button variant="outline" size="sm" disabled={!hasNext} onClick={goNext}>
                  Selanjutnya
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Silent Key?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget && (
                <>
                  Nonaktifkan Silent Key untuk <strong>{removeTarget.callsign}</strong> —{" "}
                  {removeTarget.nama_lengkap}?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Batal</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={removing}
              onClick={() => void confirmRemove()}
            >
              {removing ? "Menghapus..." : "Hapus"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tambah Silent Key</DialogTitle>
            <DialogDescription>
              Cari callsign yang belum Silent Key, lalu konfirmasi penambahan.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Callsign (contoh: YB0DX)"
                value={pickSearch}
                onChange={(e) => setPickSearch(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && runPickSearch()}
                className="font-mono"
              />
              <Button type="button" variant="secondary" onClick={runPickSearch} disabled={pickLoading}>
                {pickLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            {pickResults.length > 0 && (
              <div className="rounded-md border max-h-40 overflow-y-auto">
                {pickResults.map((m) => (
                  <button
                    key={m._id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b last:border-b-0"
                    onClick={() => openDetailForMember(m)}
                  >
                    <span className="font-mono font-medium">{m.callsign}</span>
                    <span className="text-muted-foreground"> — {m.nama_lengkap}</span>
                  </button>
                ))}
              </div>
            )}

            {selectedMember && (
              <div className="rounded-lg border p-3 space-y-2 text-sm">
                <p className="font-semibold">Ringkasan</p>
                {detailLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : detailUser ? (
                  <ul className="space-y-1 text-muted-foreground">
                    <li>
                      <span className="text-foreground font-medium">Callsign:</span> {detailUser.callsign}
                    </li>
                    <li>
                      <span className="text-foreground font-medium">Nama:</span> {detailUser.name}
                    </li>
                    <li>
                      <span className="text-foreground font-medium">Email:</span> {detailUser.email || "—"}
                    </li>
                    <li>
                      <span className="text-foreground font-medium">Provinsi:</span>{" "}
                      {detailUser.provinsi || "—"}
                    </li>
                    <li>
                      <span className="text-foreground font-medium">Kabupaten:</span>{" "}
                      {detailUser.kabupaten || "—"}
                    </li>
                    <li>
                      <span className="text-foreground font-medium">Status callsign:</span>{" "}
                      {detailUser.status_callsign || "—"}
                    </li>
                  </ul>
                ) : (
                  <p className="text-muted-foreground">Pilih anggota dari hasil pencarian.</p>
                )}
              </div>
            )}

            <div className="grid gap-3">
              <div className="grid gap-1">
                <div className="text-sm font-medium">Source trusted information</div>
                <Input
                  value={reportBy}
                  onChange={(e) => setReportBy(e.target.value)}
                  placeholder="Sumber informasi"
                />
              </div>
              <div className="grid gap-1">
                <div className="text-sm font-medium">Tanggal report</div>
                <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
              </div>
              <div className="grid gap-1">
                <div className="text-sm font-medium">Notes</div>
                <Textarea value={reportNotes} onChange={(e) => setReportNotes(e.target.value)} placeholder="Catatan (opsional)" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Batal
            </Button>
            <Button onClick={confirmAdd} disabled={!selectedMember || !detailUser || adding}>
              {adding ? "Menyimpan..." : "Konfirmasi & tambah"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
